import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Slider } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import AuthContext from '../../context/AuthContext';
import { useToast } from '../../hook/useToast';
import httpcommon from '../../helper/httpcommon';
import CommonButtonActionButton from '../common/CommonButtonActionButton';

const NAVY = '#292D5B';
const ORANGE = '#F6A03A';

const styles = {
  backBtn: {
    padding: 0,
    marginRight: '10px',
    width: '32px',
    height: '32px',
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    boxShadow: '0px 4px 12px rgba(41, 45, 91, 0.10)',
    '&:hover': { backgroundColor: '#F4FAFB' },
  },
  backIcon: { fontSize: '20px', color: NAVY },
};

const SUBJECT_LIBRARY = [
  'English Literature',
  'Grammar',
  'Math',
  'Physics',
  'Chemistry',
  'Biology',
  'History',
  'Geography',
  'Science',
  'Social Science',
];

// A subject rated 1-10 is scaled straight onto its 100-mark total (rating 5 -> 50 marks).
const DEFAULT_RATING = 5;
const defaultMarksRow = () => {
  const computed = String(Math.round((DEFAULT_RATING / 10) * 100));
  return { full_marks: '100', rating: DEFAULT_RATING, exam1: computed, exam2: computed, exam3: computed };
};

// Map a favourite subject name to the board's canonical marks key (or null).
const CBSE_ALIASES = {
  mathematics: 'math', maths: 'math', math: 'math',
  english: 'english', hindi: 'english',
  science: 'science', physics: 'science', chemistry: 'science', biology: 'science',
  'social science': 'social_science', history: 'social_science', geography: 'social_science',
  economics: 'social_science', 'political science': 'social_science', civics: 'social_science',
};
const ICSE_ALIASES = {
  english: 'literature', 'english literature': 'literature', literature: 'literature',
  grammar: 'grammar', history: 'history', geography: 'geography',
  mathematics: 'math', maths: 'math', math: 'math',
  physics: 'physics', chemistry: 'chemistry', biology: 'biology', science: 'physics',
};
const toBoardKey = (name, board) => {
  const n = String(name || '').trim().toLowerCase();
  return (board === 'ICSE' ? ICSE_ALIASES : CBSE_ALIASES)[n] || null;
};

const subjectEmoji = (name = '') => {
  const n = name.toLowerCase();
  if (/math/.test(n)) return '📐';
  if (/physic/.test(n)) return '⚛️';
  if (/chem/.test(n)) return '🧪';
  if (/bio/.test(n)) return '🧬';
  if (/science/.test(n)) return '🔬';
  if (/social|history|civic|geograph|polit/.test(n)) return '🌍';
  if (/english|literature|grammar/.test(n)) return '📖';
  if (/hindi|sanskrit|marathi|french|german|spanish|urdu|language/.test(n)) return '🗣️';
  if (/computer|informat|\bit\b/.test(n)) return '💻';
  if (/econom|business|account|commerce/.test(n)) return '📊';
  if (/art|craft|draw|paint/.test(n)) return '🎨';
  if (/music/.test(n)) return '🎵';
  if (/physical|sport|health/.test(n)) return '⚽';
  return '📘';
};

const subjectAccent = (name = '') => {
  const n = name.toLowerCase();
  if (/math|physic/.test(n)) return '#F6A03A';
  if (/chem|bio|science/.test(n)) return '#0E9BA6';
  if (/social|history|geograph|civic|polit/.test(n)) return '#7C5CBF';
  if (/english|hindi|sanskrit|language|literature|grammar/.test(n)) return '#3B7DD8';
  return '#5B6BB0';
};

const UploadMarksForm = ({ onComplete }) => {
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [board, setBoard] = useState('CBSE'); // resolved from profile, not shown
  const [favourites, setFavourites] = useState([]);
  const [rows, setRows] = useState({}); // { [subjectName]: {full_marks, rating, exam1, exam2, exam3} }
  const [loading, setLoading] = useState(false);
  const [markErrors, setMarkErrors] = useState([]);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);

  // Resolve board + prefill any previously-picked favourites.
  useEffect(() => {
    let cancelled = false;

    httpcommon
      .get('school/student/profile/')
      .then((res) => {
        const b = String(res?.data?.board || '').toUpperCase();
        if (!cancelled && (b === 'CBSE' || b === 'ICSE')) setBoard(b);
      })
      .catch(() => {});

    httpcommon
      .get(`/school/percentile/get_marks/${user?.userInfo?.id}/`)
      .then((res) => {
        const data = res?.data?.data || {};
        if (cancelled) return;
        const fav = data.favourite_subjects;
        if (Array.isArray(fav) && fav.length) {
          setFavourites(fav);
          setRows((prev) => {
            const next = { ...prev };
            fav.forEach((name) => {
              if (!next[name]) next[name] = defaultMarksRow();
            });
            return next;
          });
        }
        const sc = String(data.student_class || '').toUpperCase();
        if (sc === 'CBSE' || sc === 'ICSE') setBoard(sc);
      })
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- Subject picker -------------------- */

  const addFavourite = (name) => {
    setFavourites((prev) => {
      if (prev.includes(name)) return prev;
      return [...prev, name];
    });
    setRows((prev) => (prev[name] ? prev : { ...prev, [name]: defaultMarksRow() }));
  };
  const removeFavourite = (name) => {
    setFavourites((prev) => prev.filter((s) => s !== name));
  };
  const moveFavourite = (index, dir) => {
    setFavourites((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  /* -------------------- Marks -------------------- */

  // A 1-10 self-rating drives all three exam scores, scaled against the
  // subject's total — e.g. rating 5 on a 100-mark total submits as 50.
  const setRating = (subject, ratingValue) => {
    setRows((prev) => {
      const row = { ...(prev[subject] || defaultMarksRow()) };
      const rating = Math.max(1, Math.min(10, Number(ratingValue) || 1));
      const total = Number(row.full_marks) || 100;
      const computed = String(Math.round((rating / 10) * total));
      row.rating = rating;
      row.exam1 = computed;
      row.exam2 = computed;
      row.exam3 = computed;
      return { ...prev, [subject]: row };
    });
    setMarkErrors((current) => current.filter((name) => name !== subject));
  };

  const rowFilled = (r = {}) =>
    ['full_marks', 'exam1', 'exam2', 'exam3'].every(
      (f) => r[f] !== '' && r[f] !== null && r[f] !== undefined
    );

  const scoresFitTotal = (r = {}) => {
    const total = Number(r.full_marks);
    return Number.isFinite(total) && total > 0 && ['exam1', 'exam2', 'exam3'].every((key) => {
      const score = Number(r[key]);
      return Number.isFinite(score) && score >= 0 && score <= total;
    });
  };

  const buildMarksPayload = () => {
    const seen = new Set();
    const out = [];
    favourites.forEach((name) => {
      const key = toBoardKey(name, board);
      const r = rows[name] || {};
      const hasAny = ['full_marks', 'exam1', 'exam2', 'exam3'].some(
        (f) => r[f] !== '' && r[f] != null
      );
      if (!key || !hasAny || seen.has(key)) return;
      seen.add(key);
      out.push({
        subject: key,
        full_marks: Number(r.full_marks || 0),
        exam1: Number(r.exam1 || 0),
        exam2: Number(r.exam2 || 0),
        exam3: Number(r.exam3 || 0),
      });
    });
    return out;
  };

  const submit = async (skipMarks = false) => {
    if (loading) return;
    if (!skipMarks && favourites.length === 0) {
      showToast('Pick at least one subject to continue.', 'error');
      return;
    }
    const incompleteSubjects = favourites.filter((name) => !rowFilled(rows[name]));
    if (!skipMarks && incompleteSubjects.length) {
      setMarkErrors(incompleteSubjects);
      setIncompleteDialogOpen(true);
      return;
    }
    const invalidScoreSubjects = favourites.filter((name) => !scoresFitTotal(rows[name]));
    if (!skipMarks && invalidScoreSubjects.length) {
      setMarkErrors(invalidScoreSubjects);
      showToast('Each subject’s rating must convert to a score between 0 and its full marks.', 'error');
      return;
    }
    const payload = {
      student_id: user?.userInfo?.user,
      session_id: user?.userInfo?.user,
      student_class: board,
      favourite_subjects: favourites,
      marks: skipMarks ? [] : buildMarksPayload(),
    };
    try {
      setLoading(true);
      await httpcommon.post('/school/percentile/submit_marks/', payload);
      if (onComplete) onComplete();
    } catch (error) {
      showToast(
        error?.response?.data?.errors?.student_class?.[0] || 'Could not save. Please try again.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================= */

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', mb: { xs: 4, sm: 2 }, boxSizing: 'border-box' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #EAF6F7 0%, #F4FAFB 100%)',
          border: '1px solid #D6EAEC',
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.75, sm: 2 },
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
        }}
      >
        <IconButton onClick={() => navigate(-1)} sx={styles.backBtn}>
          <ArrowBack sx={styles.backIcon} />
        </IconButton>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '15px', color: NAVY }}>
            Choose the subjects applicable to you
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#3E4A66', mt: 0.25, lineHeight: 1.5 }}>
            Select the subjects applicable to you and then rate them as per your performance in school exam.
          </Typography>
        </Box>
      </Box>

      {/* ── Quick note ─────────────────────────────── */}
      {/* <Box sx={{ mt: 2, p: { xs: 1.35, sm: 1.65 }, borderLeft: `3px solid ${ORANGE}`, borderRadius: '0 12px 12px 0', backgroundColor: '#FFF3E1' }}>
        <Typography sx={{ color: NAVY, fontSize: 13.5, fontWeight: 800, mb: 0.55 }}>A quick note on marks</Typography>
        <Typography sx={{ color: '#3E4A66', fontSize: 12.5, lineHeight: 1.55, mb: 1 }}>We just ask you to rate yourself from 1 to 10 in each subject — this helps us understand subject strengths and interests, never to judge you.</Typography>
        <Typography sx={{ color: '#59617A', fontSize: 11.5, mt: 1 }}>Pick a subject on the left to add it, then use its slider to rate yourself.</Typography>
      </Box> */}

      {/* ── Subject picker ─────────────────────────── */}
      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {/* ALL SUBJECTS */}
        <Box
          sx={{
            border: '1px solid #E7EAF1',
            borderRadius: '18px',
            backgroundColor: '#fff',
            boxShadow: '0 6px 18px rgba(41, 45, 91, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              px: 2.25,
              py: 1.5,
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: '0.6px',
              color: '#8A90A3',
              borderBottom: '1px solid #EEF0F5',
            }}
          >
            ALL SUBJECTS
          </Typography>
          <Box sx={{ p: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
            {SUBJECT_LIBRARY.map((name) => {
              const picked = favourites.includes(name);
              const disabled = picked;
              return (
                <Box
                  key={name}
                  onClick={() => !disabled && addFavourite(name)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '12px',
                    cursor: disabled ? 'default' : 'pointer',
                    border: '1.5px solid',
                    borderColor: picked ? '#CFE9D4' : '#EEF0F5',
                    backgroundColor: picked ? '#F2FBF4' : '#fff',
                    opacity: 1,
                    transition: 'all .15s ease',
                    '&:hover': disabled ? {} : { borderColor: ORANGE, backgroundColor: '#FFF9F0' },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: 600, color: picked ? '#2E7D32' : '#2C3350' }}
                    noWrap
                  >
                    {name}
                  </Typography>
                  {picked ? (
                    <CheckRoundedIcon sx={{ fontSize: 18, color: '#2E9E5B', flexShrink: 0 }} />
                  ) : (
                    <AddRoundedIcon sx={{ fontSize: 18, color: ORANGE, flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Selected subjects */}
        <Box
          sx={{
            border: '1px solid #E7EAF1',
            borderRadius: '18px',
            backgroundColor: '#fff',
            boxShadow: '0 6px 18px rgba(41, 45, 91, 0.05)',
            overflow: 'hidden',
            alignSelf: 'flex-start',
          }}
        >
          <Typography
            sx={{
              px: 2.25,
              py: 1.5,
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: '0.6px',
              color: '#8A90A3',
              borderBottom: '1px solid #EEF0F5',
            }}
          >
            YOUR SELECTED SUBJECTS
          </Typography>
          <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {favourites.map((name, i) => {
              const row = rows[name] || {};
              const accent = subjectAccent(name);
              const rating = Number(row.rating) || DEFAULT_RATING;
              const errored = markErrors.includes(name);
              return (
                <Box
                  key={name}
                  sx={{
                    borderRadius: '14px',
                    border: `1.5px solid ${errored ? '#F3B7A8' : '#FBE3C2'}`,
                    backgroundColor: errored ? '#FFF6F4' : '#FFF9F0',
                    overflow: 'hidden',
                    transition: 'border-color .15s ease, background-color .15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.15 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        borderRadius: '8px',
                        backgroundColor: ORANGE,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: NAVY }} noWrap>
                      {name}
                    </Typography>
                    <IconButton size="small" onClick={() => moveFavourite(i, -1)} disabled={i === 0} sx={{ p: 0.25 }}>
                      <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18, color: i === 0 ? '#C4C9D6' : '#7A8096' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveFavourite(i, 1)}
                      disabled={i === favourites.length - 1}
                      sx={{ p: 0.25 }}
                    >
                      <KeyboardArrowDownRoundedIcon
                        sx={{ fontSize: 18, color: i === favourites.length - 1 ? '#C4C9D6' : '#7A8096' }}
                      />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeFavourite(name)} sx={{ p: 0.25 }}>
                      <CloseRoundedIcon sx={{ fontSize: 17, color: '#B0B5C3' }} />
                    </IconButton>
                  </Box>

                  {/* Inline self-rating */}
                  <Box sx={{ px: 1.75, pb: 1.5, pt: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#8A90A3',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 13 }}>{subjectEmoji(name)}</Box>
                        Rate yourself
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: accent }}>
                        {rating}/10
                      </Typography>
                    </Box>
                    <Slider
                      value={rating}
                      min={1}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                      onChange={(event, value) => setRating(name, value)}
                      sx={{
                        color: accent,
                        height: 6,
                        py: 0.75,
                        '& .MuiSlider-thumb': {
                          height: 18,
                          width: 18,
                          backgroundColor: '#fff',
                          border: `3px solid ${accent}`,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          transition: 'box-shadow .15s ease',
                          '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 7px ${accent}29` },
                          '&.Mui-active': { boxShadow: `0 0 0 10px ${accent}33` },
                        },
                        '& .MuiSlider-track': { border: 'none' },
                        '& .MuiSlider-rail': { backgroundColor: '#EAECF3', opacity: 1 },
                        '& .MuiSlider-valueLabel': {
                          backgroundColor: NAVY,
                          borderRadius: '8px',
                          fontSize: 11,
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            <Box sx={{ mt: 1, px: 0.5 }}>
              <Box sx={{ height: 7, borderRadius: 99, backgroundColor: '#EAECF3', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: favourites.length ? '100%' : '0%',
                    borderRadius: 99,
                    background: 'linear-gradient(90deg, #F6A03A 0%, #FFC873 100%)',
                    transition: 'width .35s ease',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#8A90A3', mt: 0.75 }}>
                {favourites.length} subject{favourites.length === 1 ? '' : 's'} selected
                {favourites.length === 0 ? ' · pick at least one to continue' : ' · add as many as apply to you'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: '1px solid #EEF0F5',
          display: favourites.length > 0 ? 'flex' : 'none',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 12.5, color: '#8A90A3', fontWeight: 600 }}>
          {markErrors.length > 0
            ? `Complete the rating for ${markErrors.length} remaining subject${markErrors.length === 1 ? '' : 's'}.`
            : `${favourites.length} subject${favourites.length === 1 ? '' : 's'} ready to submit.`}
        </Typography>
        <CommonButtonActionButton
          onClick={() => submit()}
          disabled={loading || favourites.length === 0}
          sx={{
            minWidth: 210,
            borderRadius: '14px',
            py: 1.2,
            px: 4,
            fontWeight: 800,
            fontSize: 15,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            boxShadow: loading ? 'none' : '0 12px 26px rgba(246, 160, 58, 0.34)',
          }}
        >
          {loading ? 'Saving…' : (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              Save &amp; continue <EastRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
          )}
        </CommonButtonActionButton>
      </Box>

      <Dialog open={incompleteDialogOpen} onClose={() => setIncompleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>A few ratings are missing</DialogTitle>
        <DialogContent><Typography sx={{ color: '#3E4A66', fontSize: 14, lineHeight: 1.65 }}>Please rate every selected subject before continuing. Missing rows are highlighted for you.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setIncompleteDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 800, backgroundColor: ORANGE, '&:hover': { backgroundColor: '#E8912B' } }}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadMarksForm;
